import { openSafeExternalUrl } from '../../utils/safeNavigation';
import { brandPalette as palette } from '../../utils/brandPalette';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    Typography,
    Button,
    Avatar,
    Chip,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemAvatar,
    ListItemText,
    Tabs,
    Tab,
    Badge,
    alpha,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
} from '@mui/material';
import {
    Notifications as NotificationsIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Event as EventIcon,
    People as PeopleIcon,
    Schedule as ScheduleIcon,
    AutoAwesome as AiIcon,
    Email as InboxIcon,
    Send as SendIcon,
    FiberManualRecord as LiveIcon,
    Menu as MoreVertIcon,
    Check as DoneIcon,
    TaskAlt as DoneAllIcon,
    Refresh as RefreshIcon,
    Close as CloseIcon,
    Chat as ChatIcon,
    Visibility as VisibilityIcon,
    ArrowBack as ArrowBackIcon,
    CampaignRounded as AnnouncementIcon,
    CampaignRounded as CampaignIcon,
    Info as InfoIcon,
    Business as BusinessIcon,
    Link as LinkIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../auth/AuthContext';
import Header from '../common/Header/Header';
import { db } from '../../config/firebase';
import {
    collection,
    query,
    where,
    getDocs,
    onSnapshot,
    orderBy,
    limit,
    doc,
    updateDoc,
    arrayUnion,
    writeBatch,
    deleteDoc,
    Timestamp,
    getCountFromServer,
} from 'firebase/firestore';
import toast from 'react-hot-toast';

// ============================================
// DESIGN TOKENS
// ============================================



const fontImport = `
@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;1,9..144,500&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
`;

// ============================================
// FLOATING ORBS
// ============================================

const FloatingOrbs = () => {
    const orbs = [
        { color: palette.rust, size: 420, top: '-8%', left: '4%', duration: 26, delay: 0 },
        { color: palette.pine, size: 360, top: '18%', left: '82%', duration: 32, delay: 2 },
        { color: palette.gold, size: 300, top: '58%', left: '-6%', duration: 24, delay: 1 },
        { color: palette.purple, size: 340, top: '72%', left: '70%', duration: 30, delay: 3 },
        { color: palette.teal, size: 260, top: '38%', left: '46%', duration: 28, delay: 1.5 },
    ];

    return (
        <Box
            aria-hidden="true"
            sx={{
                position: 'fixed',
                inset: 0,
                overflow: 'hidden',
                pointerEvents: 'none',
                zIndex: 0,
            }}
        >
            {orbs.map((orb, i) => (
                <motion.div
                    key={i}
                    initial={{ opacity: 0 }}
                    animate={{
                        opacity: [0.5, 0.85, 0.5],
                        x: [0, 40, -30, 0],
                        y: [0, -35, 25, 0],
                        scale: [1, 1.12, 0.94, 1],
                    }}
                    transition={{
                        duration: orb.duration,
                        delay: orb.delay,
                        repeat: Infinity,
                        ease: 'easeInOut',
                    }}
                    style={{
                        position: 'absolute',
                        top: orb.top,
                        left: orb.left,
                        width: orb.size,
                        height: orb.size,
                        borderRadius: '50%',
                        background: `radial-gradient(circle, ${orb.color}22, ${orb.color}00 70%)`,
                        filter: 'blur(10px)',
                    }}
                />
            ))}
        </Box>
    );
};

// ============================================
// NOTIFICATION LIST ITEM COMPONENT
// ============================================

const NotificationListItem = ({ notification, onAction, onAcceptDecline, onMarkRead, formatTime, getIconColor }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ 
                scale: 1.01,
                transition: { duration: 0.2 }
            }}
            onHoverStart={() => setIsHovered(true)}
            onHoverEnd={() => setIsHovered(false)}
        >
            <ListItemButton
                component="div"
                onClick={() => onAction(notification)}
                sx={{
                    p: 2.5,
                    borderBottom: `1px solid ${palette.hairline}`,
                    bgcolor: notification.read ? 'transparent' : `${getIconColor(notification.type)}06`,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    '&:hover': {
                        bgcolor: `${getIconColor(notification.type)}08`,
                        boxShadow: `inset 0 0 0 1px ${getIconColor(notification.type)}20`,
                    },
                    '&::before': notification.isPushNotification ? {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '20%',
                        bottom: '20%',
                        width: 3,
                        background: `linear-gradient(180deg, ${palette.gold}, ${palette.goldLight})`,
                        borderRadius: '0 2px 2px 0',
                        opacity: notification.read ? 0.3 : 1,
                    } : {},
                }}
            >
                <ListItemAvatar>
                    <Avatar sx={{ 
                        bgcolor: `${getIconColor(notification.type)}12`, 
                        color: getIconColor(notification.type),
                        width: 48,
                        height: 48,
                        borderRadius: '14px',
                        boxShadow: isHovered ? `0 4px 12px ${getIconColor(notification.type)}30` : 'none',
                        transition: 'box-shadow 0.3s ease',
                    }}>
                        {notification.icon}
                    </Avatar>
                </ListItemAvatar>
                <ListItemText
                    disableTypography
                    primary={
                        <Box sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1, display: 'flex' }}>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ color: palette.ink, fontSize: '0.95rem' }}>
                                {notification.title}
                            </Typography>
                            {!notification.read && (
                                <Chip 
                                    label="New" 
                                    size="small" 
                                    sx={{ 
                                        height: 20, 
                                        fontSize: '0.55rem', 
                                        bgcolor: `${palette.rust}12`, 
                                        color: palette.rust, 
                                        fontWeight: 600,
                                        borderRadius: '4px',
                                    }}
                                />
                            )}
                            {notification.isPushNotification && (
                                <Chip 
                                    label="📢 Push" 
                                    size="small" 
                                    sx={{ 
                                        height: 20, 
                                        fontSize: '0.55rem', 
                                        bgcolor: `${palette.gold}12`, 
                                        color: palette.gold, 
                                        fontWeight: 600,
                                        borderRadius: '4px',
                                    }}
                                />
                            )}
                            {notification.priority === 'high' && (
                                <Chip 
                                    label="🔥 High" 
                                    size="small" 
                                    sx={{ 
                                        height: 20, 
                                        fontSize: '0.55rem', 
                                        bgcolor: `${palette.warning}12`, 
                                        color: palette.warning, 
                                        fontWeight: 600,
                                        borderRadius: '4px',
                                    }}
                                />
                            )}
                            {notification.priority === 'urgent' && (
                                <Chip 
                                    label="🚨 Urgent" 
                                    size="small" 
                                    sx={{ 
                                        height: 20, 
                                        fontSize: '0.55rem', 
                                        bgcolor: `${palette.danger}12`, 
                                        color: palette.danger, 
                                        fontWeight: 600,
                                        borderRadius: '4px',
                                        animation: 'pulse 2s infinite',
                                    }}
                                />
                            )}
                            {notification.status && notification.status !== 'pending' && (
                                <Chip 
                                    label={notification.status} 
                                    size="small" 
                                    sx={{ 
                                        height: 20, 
                                        fontSize: '0.55rem', 
                                        bgcolor: notification.status === 'accepted' ? `${palette.pine}12` : `${palette.clay}12`,
                                        color: notification.status === 'accepted' ? palette.pine : palette.clay,
                                        fontWeight: 600,
                                        borderRadius: '4px',
                                    }}
                                />
                            )}
                        </Box>
                    }
                    secondary={
                        <Box>
                            <Typography variant="body2" sx={{ color: palette.inkSoft, fontSize: '0.85rem', mt: 0.5 }}>
                                {notification.message}
                            </Typography>
                            {notification.imageUrl && (
                                <Box sx={{ mt: 1, mb: 0.5 }}>
                                    <Box
                                        component="img"
                                        src={notification.imageUrl}
                                        alt="Notification"
                                        sx={{
                                            maxWidth: '100%',
                                            maxHeight: 120,
                                            borderRadius: '8px',
                                            border: `1px solid ${palette.hairline}`,
                                        }}
                                    />
                                </Box>
                            )}
                            <Box sx={{ alignItems: 'center', justifyContent: 'space-between', mt: 0.5, display: 'flex' }}>
                                <Typography variant="caption" sx={{ color: palette.inkLight, fontSize: '0.7rem' }}>
                                    {formatTime(notification.time)}
                                    {notification.sentBy && (
                                        <span style={{ marginLeft: 8 }}>
                                            by {notification.sentBy}
                                        </span>
                                    )}
                                    {notification.recipientCompanyName && (
                                        <span style={{ marginLeft: 8 }}>
                                            to {notification.recipientCompanyName}
                                        </span>
                                    )}
                                </Typography>
                                {notification.action === 'accept_decline' && (
                                    <Box sx={{ gap: 0.5, display: 'flex' }}>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            sx={{
                                                fontSize: '0.65rem',
                                                py: 0.3,
                                                px: 1.5,
                                                minWidth: 'auto',
                                                bgcolor: palette.pine,
                                                '&:hover': { bgcolor: palette.pineSoft }
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onAcceptDecline(notification, 'accept');
                                            }}
                                        >
                                            Accept
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                fontSize: '0.65rem',
                                                py: 0.3,
                                                px: 1.5,
                                                minWidth: 'auto',
                                                borderColor: palette.clay,
                                                color: palette.clay,
                                                '&:hover': { bgcolor: `${palette.clay}08` }
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onAcceptDecline(notification, 'decline');
                                            }}
                                        >
                                            Decline
                                        </Button>
                                    </Box>
                                )}
                                {notification.link && (
                                    <Button
                                        size="small"
                                        startIcon={<LinkIcon sx={{ fontSize: 14 }} />}
                                        sx={{
                                            fontSize: '0.6rem',
                                            color: palette.teal,
                                            textTransform: 'none',
                                            '&:hover': { bgcolor: `${palette.teal}08` }
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openSafeExternalUrl(notification.link);
                                        }}
                                    >
                                        Open Link
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    }
                />
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        onMarkRead(notification);
                    }}
                    sx={{
                        color: notification.read ? palette.inkLight : palette.rust,
                        opacity: isHovered ? 1 : notification.read ? 0.3 : 0.6,
                        transition: 'opacity 0.3s ease',
                    }}
                >
                    {notification.read ? <DoneIcon /> : <DoneAllIcon />}
                </IconButton>
            </ListItemButton>
        </motion.div>
    );
};

// ============================================
// NOTIFICATIONS PAGE
// ============================================

const NotificationsPage = () => {
    const navigate = useNavigate();
    const { currentUser, userData, companyData } = useAuth();
    const [loading, setLoading] = useState(true);
    const [notifications, setNotifications] = useState([]);
    const [filteredNotifications, setFilteredNotifications] = useState([]);
    const [tabValue, setTabValue] = useState(0);
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);

    // ============================================
    // GET COMPANY ID
    // ============================================

    const getCompanyId = () => {
        return companyData?.companyId || userData?.companyId || companyData?.id || userData?.id || currentUser?.uid;
    };

    const isReadForViewer = (data = {}) => {
        const uid = currentUser?.uid;
        if (data.read === true) return true; // backward compatibility with legacy records
        return Boolean(uid && Array.isArray(data.readBy) && data.readBy.includes(uid));
    };

    const sourceDocumentId = (notification) => String(notification?.id || '').replace(/^(inv_|sent_|ai_acc_|meeting_|push_|comp_)/, '');

    // ============================================
    // FORMAT TIME
    // ============================================

    const formatTime = (date) => {
        if (!date) return 'Just now';
        const d = date instanceof Date ? date : date.toDate?.() || new Date(date);
        const now = new Date();
        const diff = now - d;
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);
        
        if (minutes < 1) return 'Just now';
        if (minutes < 60) return `${minutes}m ago`;
        if (hours < 24) return `${hours}h ago`;
        if (days < 7) return `${days}d ago`;
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    // ============================================
    // GET ICON COLOR
    // ============================================

    const getIconColor = (type) => {
        switch (type) {
            case 'invitation': return palette.gold;
            case 'invitation_accepted': return palette.pine;
            case 'invitation_sent': return palette.teal;
            case 'ai_sent': return palette.purple;
            case 'ai_accepted': return palette.pine;
            case 'meeting_request': return palette.rust;
            case 'push_notification': return palette.gold;
            case 'announcement': return palette.rust;
            case 'promotion': return palette.gold;
            case 'company_notification': return palette.teal;
            case 'info': return palette.blue;
            case 'warning': return palette.warning;
            case 'error': return palette.danger;
            default: return palette.inkSoft;
        }
    };

    // ============================================
    // GET ICON FOR NOTIFICATION
    // ============================================

    const getNotificationIcon = (notification) => {
        if (notification.icon) return notification.icon;

        switch (notification.type) {
            case 'invitation':
                return <InboxIcon />;
            case 'invitation_accepted':
            case 'ai_accepted':
                return <CheckCircleIcon />;
            case 'invitation_sent':
                return <SendIcon />;
            case 'ai_sent':
                return <AiIcon />;
            case 'meeting_request':
                return <EventIcon />;
            case 'push_notification':
                return <NotificationsIcon />;
            case 'announcement':
                return <AnnouncementIcon />;
            case 'promotion':
                return <CampaignIcon />;
            case 'company_notification':
                return <BusinessIcon />;
            case 'info':
                return <InfoIcon />;
            case 'warning':
                return <ScheduleIcon />;
            case 'error':
                return <CancelIcon />;
            default:
                return <NotificationsIcon />;
        }
    };

    // ============================================
    // FETCH NOTIFICATIONS (ALL SOURCES)
    // ============================================

    const fetchNotifications = async () => {
        setLoading(true);
        try {
            const companyId = getCompanyId();
            const targetId = companyId || currentUser?.uid;
            
            if (!targetId) {
                setNotifications([]);
                setFilteredNotifications([]);
                setUnreadCount(0);
                setLoading(false);
                return;
            }

            const allNotifications = [];

            // 1. FETCH INVITATIONS (Received)
            try {
                const invitationsRef = collection(db, 'invitations');
                const receivedQuery = query(
                    invitationsRef,
                    where('targetCompanyId', '==', targetId),
                    orderBy('createdAt', 'desc')
                );
                const receivedSnapshot = await getDocs(receivedQuery);
                
                receivedSnapshot.forEach((doc) => {
                    const data = doc.data();
                    const status = data.status || 'pending';
                    allNotifications.push({
                        id: `inv_${doc.id}`,
                        title: status === 'pending' ? '📩 New Invitation Received' : '✅ Invitation Accepted',
                        message: status === 'pending' 
                            ? `${data.companyName || 'A company'} sent you a connection request`
                            : `${data.companyName || 'A company'} accepted your connection request`,
                        time: data.updatedAt?.toDate?.() || data.createdAt?.toDate?.() || new Date(),
                        type: status === 'pending' ? 'invitation' : 'invitation_accepted',
                        read: isReadForViewer(data),
                        icon: status === 'pending' ? <InboxIcon /> : <CheckCircleIcon />,
                        data: data,
                        status: status,
                        action: status === 'pending' ? 'accept_decline' : 'view',
                        isPushNotification: false,
                        source: 'invitations',
                    });
                });
            } catch (err) {
                console.warn('Error fetching received invitations:', err);
            }

            // 2. FETCH SENT INVITATIONS (AI + Manual)
            try {
                const invitationsRef = collection(db, 'invitations');
                const sentQuery = query(
                    invitationsRef,
                    where('companyId', '==', targetId),
                    orderBy('createdAt', 'desc')
                );
                const sentSnapshot = await getDocs(sentQuery);
                
                sentSnapshot.forEach((doc) => {
                    const data = doc.data();
                    const status = data.status || 'pending';
                    const isAI = data.autoGenerated === true;
                    
                    if (status === 'pending') {
                        allNotifications.push({
                            id: `sent_${doc.id}`,
                            title: isAI ? '🤖 AI Invitation Sent' : '📤 Invitation Sent',
                            message: isAI 
                                ? `AI invitation sent to ${data.targetCompanyName || 'a company'} (${Math.round(data.aiScore || 0)}% match)`
                                : `Invitation sent to ${data.targetCompanyName || 'a company'}`,
                            time: data.createdAt?.toDate?.() || new Date(),
                            type: isAI ? 'ai_sent' : 'invitation_sent',
                            read: isReadForViewer(data),
                            icon: isAI ? <AiIcon /> : <SendIcon />,
                            data: data,
                            status: status,
                            action: 'view',
                            isPushNotification: false,
                            source: 'invitations',
                        });
                    } else if (status === 'accepted' && isAI) {
                        allNotifications.push({
                            id: `ai_acc_${doc.id}`,
                            title: '🤖 AI Match Connected!',
                            message: `${data.targetCompanyName || 'A company'} accepted your AI invitation`,
                            time: data.updatedAt?.toDate?.() || data.createdAt?.toDate?.() || new Date(),
                            type: 'ai_accepted',
                            read: isReadForViewer(data),
                            icon: <AiIcon />,
                            data: data,
                            status: status,
                            action: 'chat',
                            isPushNotification: false,
                            source: 'invitations',
                        });
                    }
                });
            } catch (err) {
                console.warn('Error fetching sent invitations:', err);
            }

            // 3. FETCH MEETING REQUESTS
            try {
                const meetingsRef = collection(db, 'meetings');
                const meetingQuery = query(
                    meetingsRef,
                    where('targetCompanyId', '==', targetId),
                    where('status', '==', 'pending'),
                    orderBy('createdAt', 'desc')
                );
                const meetingSnapshot = await getDocs(meetingQuery);
                
                meetingSnapshot.forEach((doc) => {
                    const data = doc.data();
                    allNotifications.push({
                        id: `meeting_${doc.id}`,
                        title: '📅 Meeting Request',
                        message: `${data.requesterName || 'A company'} requested a meeting`,
                        time: data.createdAt?.toDate?.() || new Date(),
                        type: 'meeting_request',
                        read: isReadForViewer(data),
                        icon: <EventIcon />,
                        data: data,
                        status: 'pending',
                        action: 'view_meeting',
                        isPushNotification: false,
                        source: 'meetings',
                    });
                });
            } catch (err) {
                console.warn('Error fetching meeting requests:', err);
            }

            // 4. FETCH PUSH NOTIFICATIONS (Global)
            try {
                const notificationsRef = collection(db, 'notifications');
                const notifQuery = query(
                    notificationsRef,
                    where('userId', 'in', ['all', targetId]),
                    orderBy('createdAt', 'desc')
                );
                const notifSnapshot = await getDocs(notifQuery);
                
                notifSnapshot.forEach((doc) => {
                    const data = doc.data();
                    const isForCompany = data.userId === 'all' || 
                                       data.userId === targetId || 
                                       (data.targetCompanyId && data.targetCompanyId === companyId);
                    
                    if (isForCompany) {
                        const notificationType = data.type || 'push_notification';
                        allNotifications.push({
                            id: `push_${doc.id}`,
                            title: data.title || 'New Notification',
                            message: data.message || '',
                            time: data.createdAt?.toDate?.() || new Date(),
                            type: notificationType,
                            read: isReadForViewer(data),
                            icon: getNotificationIcon({ type: notificationType }),
                            imageUrl: data.imageUrl || null,
                            link: data.link || null,
                            priority: data.priority || 'normal',
                            sentBy: data.sentBy || 'Admin',
                            recipientCompanyName: data.recipientCompanyName || null,
                            data: data,
                            action: 'view_notification',
                            isPushNotification: true,
                            source: 'notifications',
                        });
                    }
                });
            } catch (err) {
                console.warn('Error fetching push notifications:', err);
            }

            // 5. FETCH COMPANY-SPECIFIC NOTIFICATIONS
            if (companyId) {
                try {
                    const companyNotifsRef = collection(db, 'companyNotifications');
                    const companyQuery = query(
                        companyNotifsRef,
                        where('companyId', '==', companyId),
                        orderBy('createdAt', 'desc')
                    );
                    const companySnapshot = await getDocs(companyQuery);

                    companySnapshot.forEach((doc) => {
                        const data = doc.data();
                        allNotifications.push({
                            id: `comp_${doc.id}`,
                            title: data.title || 'Company Update',
                            message: data.message || '',
                            time: data.createdAt?.toDate?.() || new Date(),
                            type: data.type || 'company_notification',
                            read: isReadForViewer(data),
                            icon: <BusinessIcon />,
                            imageUrl: data.imageUrl || null,
                            link: data.link || null,
                            priority: data.priority || 'normal',
                            sentBy: data.sentBy || 'Admin',
                            data: data,
                            action: 'view_company_notification',
                            isPushNotification: true,
                            source: 'companyNotifications',
                        });
                    });
                } catch (err) {
                    console.warn('Error fetching company notifications:', err);
                }
            }

            // Sort by time (newest first) and remove duplicates
            allNotifications.sort((a, b) => b.time - a.time);
            
            // Remove duplicates by data reference
            const uniqueNotifications = [];
            const seenRefs = new Set();
            for (const notif of allNotifications) {
                const ref = notif.data?.id || notif.id;
                if (!seenRefs.has(ref)) {
                    seenRefs.add(ref);
                    uniqueNotifications.push(notif);
                }
            }

            setNotifications(uniqueNotifications);
            setFilteredNotifications(uniqueNotifications);
            
            const unread = uniqueNotifications.filter(n => !n.read).length;
            setUnreadCount(unread);

        } catch (error) {
            console.error('Error fetching notifications:', error);
            toast.error('Failed to load notifications');
        } finally {
            setLoading(false);
        }
    };

    // ============================================
    // REAL-TIME LISTENERS
    // ============================================

    useEffect(() => {
        fetchNotifications();

        const companyId = getCompanyId();
        const targetId = companyId || currentUser?.uid;

        if (targetId) {
            const unsubscribers = [];

            // Listen to invitations
            try {
                const invitationsRef = collection(db, 'invitations');
                const inviteQuery = query(
                    invitationsRef,
                    where('targetCompanyId', '==', targetId)
                );
                const unsubInvites = onSnapshot(inviteQuery, () => {
                    fetchNotifications();
                });
                unsubscribers.push(unsubInvites);

                const sentQuery = query(
                    invitationsRef,
                    where('companyId', '==', targetId)
                );
                const unsubSent = onSnapshot(sentQuery, () => {
                    fetchNotifications();
                });
                unsubscribers.push(unsubSent);
            } catch (err) {
                console.warn('Invitations listener error:', err);
            }

            // Listen to meetings
            try {
                const meetingsRef = collection(db, 'meetings');
                const meetingQuery = query(
                    meetingsRef,
                    where('targetCompanyId', '==', targetId),
                    where('status', '==', 'pending')
                );
                const unsubMeetings = onSnapshot(meetingQuery, () => {
                    fetchNotifications();
                });
                unsubscribers.push(unsubMeetings);
            } catch (err) {
                console.warn('Meetings listener error:', err);
            }

            // Listen to push notifications
            try {
                const notificationsRef = collection(db, 'notifications');
                const notifQuery = query(
                    notificationsRef,
                    where('userId', 'in', ['all', targetId])
                );
                const unsubNotifs = onSnapshot(notifQuery, () => {
                    fetchNotifications();
                });
                unsubscribers.push(unsubNotifs);
            } catch (err) {
                console.warn('Notifications listener error:', err);
            }

            // Listen to company notifications
            if (companyId) {
                try {
                    const companyNotifsRef = collection(db, 'companyNotifications');
                    const companyQuery = query(
                        companyNotifsRef,
                        where('companyId', '==', companyId)
                    );
                    const unsubCompany = onSnapshot(companyQuery, () => {
                        fetchNotifications();
                    });
                    unsubscribers.push(unsubCompany);
                } catch (err) {
                    console.warn('Company notifications listener error:', err);
                }
            }

            return () => {
                unsubscribers.forEach(unsub => {
                    try { unsub(); } catch (e) {}
                });
            };
        }
    }, [companyData, userData, currentUser]);

    // ============================================
    // HANDLE TAB CHANGE
    // ============================================

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        if (newValue === 0) {
            setFilteredNotifications(notifications);
        } else if (newValue === 1) {
            setFilteredNotifications(notifications.filter(n => !n.read));
        } else if (newValue === 2) {
            setFilteredNotifications(notifications.filter(n => 
                n.type === 'invitation' || 
                n.type === 'invitation_accepted' || 
                n.type === 'invitation_sent'
            ));
        } else if (newValue === 3) {
            setFilteredNotifications(notifications.filter(n => 
                n.type === 'meeting_request'
            ));
        } else if (newValue === 4) {
            setFilteredNotifications(notifications.filter(n => 
                n.type === 'ai_sent' || n.type === 'ai_accepted'
            ));
        } else if (newValue === 5) {
            setFilteredNotifications(notifications.filter(n => 
                n.isPushNotification === true
            ));
        }
    };

    // ============================================
    // HANDLE MARK AS READ
    // ============================================

    const handleMarkAsRead = async (notification) => {
        try {
            // Update local state
            setNotifications(prev => 
                prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
            );
            setFilteredNotifications(prev => 
                prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
            );
            setUnreadCount(prev => Math.max(0, prev - 1));

            // Persist read state per signed-in user. A company/global notification
            // must not become read for every team member when one person opens it.
            if (notification.source && currentUser?.uid) {
                try {
                    const docRef = doc(db, notification.source, sourceDocumentId(notification));
                    await updateDoc(docRef, { readBy: arrayUnion(currentUser.uid) });
                } catch (err) {
                    console.warn('Unable to persist notification read state:', err?.message || err);
                }
            }
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    // ============================================
    // HANDLE MARK ALL AS READ
    // ============================================

    const handleMarkAllAsRead = async () => {
        const uid = currentUser?.uid;
        const pending = notifications.filter((notification) => !notification.read && notification.source);
        if (!uid || !pending.length) return;
        try {
            for (let offset = 0; offset < pending.length; offset += 450) {
                const batch = writeBatch(db);
                pending.slice(offset, offset + 450).forEach((notification) => {
                    batch.update(doc(db, notification.source, sourceDocumentId(notification)), { readBy: arrayUnion(uid) });
                });
                await batch.commit();
            }
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setFilteredNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setUnreadCount(0);
            toast.success('All notifications marked as read');
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
            toast.error('Unable to mark all notifications as read.');
            await fetchNotifications();
        }
    };

    // ============================================
    // HANDLE ACCEPT/DECLINE
    // ============================================

    const handleAcceptDecline = async (notification, action) => {
        if (processing) return;
        setProcessing(true);
        
        try {
            const invitationRef = doc(db, 'invitations', notification.id.replace(/^inv_/, ''));
            await updateDoc(invitationRef, {
                status: action === 'accept' ? 'accepted' : 'declined',
                updatedAt: Timestamp.now()
            });
            
            toast.success(action === 'accept' ? 'Invitation accepted!' : 'Invitation declined');
            
            setNotifications(prev => 
                prev.map(n => n.id === notification.id ? { ...n, read: true, status: action === 'accept' ? 'accepted' : 'declined' } : n)
            );
            setFilteredNotifications(prev => 
                prev.map(n => n.id === notification.id ? { ...n, read: true, status: action === 'accept' ? 'accepted' : 'declined' } : n)
            );
            
            setSelectedNotification(null);
            setDetailDialogOpen(false);
            
        } catch (error) {
            console.error('Error handling invitation:', error);
            toast.error('Failed to process invitation');
        } finally {
            setProcessing(false);
        }
    };

    // ============================================
    // HANDLE NOTIFICATION CLICK
    // ============================================

    const handleNotificationClick = (notification) => {
        setSelectedNotification(notification);
        setDetailDialogOpen(true);
        if (!notification.read) {
            handleMarkAsRead(notification);
        }
    };

    // ============================================
    // HANDLE ACTION
    // ============================================

    const handleAction = (notification, action) => {
        if (action === 'accept' || action === 'decline') {
            handleAcceptDecline(notification, action);
            return;
        }

        setDetailDialogOpen(false);
        
        switch (action) {
            case 'view':
            case 'view_company_notification':
                if (notification.data?.companyId || notification.data?.targetCompanyId) {
                    const companyId = notification.data?.companyId || notification.data?.targetCompanyId;
                    navigate(`/dashboard/company/${companyId}`);
                } else {
                    navigate('/dashboard/companies');
                }
                break;
            case 'view_notification':
                if (notification.link) {
                    openSafeExternalUrl(notification.link);
                } else if (notification.data?.link) {
                    openSafeExternalUrl(notification.data.link);
                } else {
                    navigate('/dashboard');
                }
                break;
            case 'chat':
                navigate('/dashboard/messages');
                break;
            case 'view_meeting':
                navigate('/dashboard/meetings');
                break;
            default:
                navigate('/dashboard');
        }
    };

    // ============================================
    // RENDER
    // ============================================

    const canvasSx = {
        minHeight: '100vh',
        position: 'relative',
        bgcolor: palette.canvas,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        backgroundImage: `
            radial-gradient(circle at 8% 0%, rgba(193,84,31,0.08), transparent 40%),
            radial-gradient(circle at 96% 8%, rgba(47,89,66,0.06), transparent 38%),
            radial-gradient(circle at 50% 100%, rgba(184,132,42,0.04), transparent 42%)
        `,
        backgroundSize: '200% 200%',
        animation: 'auroraDrift 34s ease-in-out infinite',
        '&::before': {
            content: '""',
            position: 'fixed',
            inset: 0,
            pointerEvents: 'none',
            opacity: 0.3,
            backgroundImage:
                'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27120%27 height=%27120%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.9%27 numOctaves=%272%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27 opacity=%270.025%27/%3E%3C/svg%3E")',
            animation: 'grainDrift 9s steps(6) infinite',
        },
    };

    return (
        <Box className="erp-page-shell erp-internal-page" sx={canvasSx}>
            <style>{fontImport}</style>
            
            <Header />

            <Container maxWidth="lg" sx={{ py: 4, position: 'relative', zIndex: 1 }}>
                {/* Header */}
                <Box className="erp-page-hero" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton onClick={() => navigate('/dashboard')} sx={{ color: palette.inkSoft }}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Box>
                            <Typography sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 700, fontSize: '1.8rem', color: palette.ink }}>
                                Notifications
                            </Typography>
                            <Typography sx={{ color: palette.inkSoft, fontSize: '0.9rem' }}>
                                Stay updated with all your activity
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        <Chip
                            icon={<LiveIcon sx={{ fontSize: 10, color: `${palette.pine} !important` }} />}
                            label={`${unreadCount} unread`}
                            sx={{
                                bgcolor: 'rgba(47,89,66,0.08)',
                                color: palette.pine,
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                height: 32,
                                borderRadius: '16px',
                            }}
                        />
                        <Button
                            variant="outlined"
                            startIcon={<DoneAllIcon />}
                            onClick={handleMarkAllAsRead}
                            sx={{
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                borderColor: palette.hairlineStrong,
                                color: palette.inkSoft,
                                '&:hover': {
                                    borderColor: palette.rust,
                                    color: palette.rust,
                                }
                            }}
                        >
                            Mark All Read
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<RefreshIcon />}
                            onClick={fetchNotifications}
                            sx={{
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                py: 0.6,
                                px: 2.5,
                                background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                                boxShadow: '0 4px 16px rgba(193,84,31,0.3)',
                                '&:hover': {
                                    boxShadow: '0 8px 24px rgba(193,84,31,0.4)',
                                    transform: 'translateY(-2px)',
                                }
                            }}
                        >
                            Refresh
                        </Button>
                    </Box>
                </Box>

                {/* Tabs */}
                <Paper sx={{ borderRadius: '16px', border: `1px solid ${palette.hairline}`, mb: 3, overflow: 'hidden' }}>
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                py: 1.5,
                                px: 2.5,
                                minHeight: 48,
                                color: palette.inkSoft,
                                '&.Mui-selected': {
                                    color: palette.rust,
                                }
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: palette.rust,
                                height: 3,
                            }
                        }}
                    >
                        <Tab label={`All (${notifications.length})`} />
                        <Tab label={`Unread (${unreadCount})`} />
                        <Tab label="Invitations" />
                        <Tab label="Meetings" />
                        <Tab label="AI Matches" />
                        <Tab label="📢 Push" />
                    </Tabs>
                </Paper>

                {/* Notifications List */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <Box sx={{ textAlign: 'center' }}>
                            <CircularProgress size={48} sx={{ color: palette.rust }} />
                            <Typography sx={{ color: palette.inkSoft, mt: 2 }}>Loading notifications...</Typography>
                        </Box>
                    </Box>
                ) : filteredNotifications.length === 0 ? (
                    <Paper sx={{ 
                        p: 6, 
                        borderRadius: '16px', 
                        border: `1px solid ${palette.hairline}`,
                        textAlign: 'center',
                        background: palette.paper
                    }}>
                        <NotificationsIcon sx={{ fontSize: 64, color: palette.inkSoft, opacity: 0.2 }} />
                        <Typography variant="h6" sx={{ color: palette.ink, mt: 2, fontWeight: 600 }}>
                            No notifications
                        </Typography>
                        <Typography sx={{ color: palette.inkSoft, mt: 1 }}>
                            {tabValue === 0 ? "You're all caught up!" : "No notifications in this category"}
                        </Typography>
                    </Paper>
                ) : (
                    <Paper sx={{ borderRadius: '16px', border: `1px solid ${palette.hairline}`, overflow: 'hidden' }}>
                        <List sx={{ p: 0 }}>
                            {filteredNotifications.map((notification, index) => (
                                <NotificationListItem
                                    key={notification.id}
                                    notification={notification}
                                    onAction={handleNotificationClick}
                                    onAcceptDecline={handleAcceptDecline}
                                    onMarkRead={handleMarkAsRead}
                                    formatTime={formatTime}
                                    getIconColor={getIconColor}
                                />
                            ))}
                        </List>
                    </Paper>
                )}
            </Container>

            {/* Notification Detail Dialog */}
            <Dialog
                open={detailDialogOpen}
                onClose={() => setDetailDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                slotProps={{ paper: {
                    sx: {
                        borderRadius: '20px',
                        border: `1px solid ${palette.hairline}`,
                        boxShadow: '0 24px 64px rgba(33,26,20,0.12)',
                        background: palette.paper,
                        overflow: 'hidden',
                    }
                } }}
            >
                {selectedNotification && (
                    <>
                        <DialogTitle sx={{
                            borderBottom: `1px solid ${palette.hairline}`,
                            pb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            background: `${getIconColor(selectedNotification.type)}06`,
                        }}>
                            <Box sx={{ alignItems: 'center', gap: 2, display: 'flex' }}>
                                <Avatar sx={{ 
                                    bgcolor: `${getIconColor(selectedNotification.type)}12`, 
                                    color: getIconColor(selectedNotification.type),
                                    width: 44,
                                    height: 44,
                                }}>
                                    {selectedNotification.icon}
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" fontWeight={700} sx={{ color: palette.ink, fontSize: '1.05rem' }}>
                                        {selectedNotification.title}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: palette.inkSoft }}>
                                        {formatTime(selectedNotification.time)}
                                    </Typography>
                                </Box>
                            </Box>
                            <IconButton onClick={() => setDetailDialogOpen(false)} size="small">
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent sx={{ pt: 3 }}>
                            <Typography variant="body1" sx={{ color: palette.ink, fontSize: '1rem', lineHeight: 1.7 }}>
                                {selectedNotification.message}
                            </Typography>
                            
                            {selectedNotification.imageUrl && (
                                <Box sx={{ mt: 2 }}>
                                    <Box
                                        component="img"
                                        src={selectedNotification.imageUrl}
                                        alt="Notification"
                                        sx={{
                                            width: '100%',
                                            maxHeight: 300,
                                            borderRadius: '12px',
                                            border: `1px solid ${palette.hairline}`,
                                            objectFit: 'cover',
                                        }}
                                    />
                                </Box>
                            )}

                            {selectedNotification.data && (
                                <Box sx={{ mt: 2, p: 2, borderRadius: '12px', bgcolor: palette.canvas, border: `1px solid ${palette.hairline}` }}>
                                    <Typography variant="caption" sx={{ color: palette.inkSoft, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                        Details
                                    </Typography>
                                    <Box sx={{ mt: 1 }}>
                                        {selectedNotification.data.companyName && (
                                            <Typography variant="body2" sx={{ color: palette.ink }}>
                                                <strong>Company:</strong> {selectedNotification.data.companyName}
                                            </Typography>
                                        )}
                                        {selectedNotification.data.targetCompanyName && (
                                            <Typography variant="body2" sx={{ color: palette.ink }}>
                                                <strong>Target:</strong> {selectedNotification.data.targetCompanyName}
                                            </Typography>
                                        )}
                                        {selectedNotification.data.aiScore && (
                                            <Typography variant="body2" sx={{ color: palette.ink }}>
                                                <strong>AI Match Score:</strong> {Math.round(selectedNotification.data.aiScore)}%
                                            </Typography>
                                        )}
                                        {selectedNotification.data.requesterName && (
                                            <Typography variant="body2" sx={{ color: palette.ink }}>
                                                <strong>Requester:</strong> {selectedNotification.data.requesterName}
                                            </Typography>
                                        )}
                                        {selectedNotification.sentBy && (
                                            <Typography variant="body2" sx={{ color: palette.ink }}>
                                                <strong>Sent By:</strong> {selectedNotification.sentBy}
                                            </Typography>
                                        )}
                                        {selectedNotification.recipientCompanyName && (
                                            <Typography variant="body2" sx={{ color: palette.ink }}>
                                                <strong>Recipient:</strong> {selectedNotification.recipientCompanyName}
                                            </Typography>
                                        )}
                                        {selectedNotification.priority && selectedNotification.priority !== 'normal' && (
                                            <Typography variant="body2" sx={{ color: palette.ink }}>
                                                <strong>Priority:</strong> <Chip 
                                                    label={selectedNotification.priority} 
                                                    size="small" 
                                                    sx={{ 
                                                        height: 20, 
                                                        fontSize: '0.55rem',
                                                        bgcolor: selectedNotification.priority === 'urgent' ? `${palette.danger}12` : `${palette.warning}12`,
                                                        color: selectedNotification.priority === 'urgent' ? palette.danger : palette.warning,
                                                    }}
                                                />
                                            </Typography>
                                        )}
                                        {selectedNotification.link && (
                                            <Typography variant="body2" sx={{ color: palette.ink }}>
                                                <strong>Link:</strong> 
                                                <Button
                                                    size="small"
                                                    startIcon={<LinkIcon sx={{ fontSize: 14 }} />}
                                                    onClick={() => openSafeExternalUrl(selectedNotification.link)}
                                                    sx={{
                                                        ml: 1,
                                                        fontSize: '0.7rem',
                                                        color: palette.teal,
                                                        textTransform: 'none',
                                                    }}
                                                >
                                                    Open Link
                                                </Button>
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions sx={{ p: 3, borderTop: `1px solid ${palette.hairline}`, gap: 1.5, flexWrap: 'wrap' }}>
                            <Button
                                variant="outlined"
                                onClick={() => setDetailDialogOpen(false)}
                                sx={{ color: palette.inkSoft, fontWeight: 600, textTransform: 'none' }}
                            >
                                Close
                            </Button>
                            {selectedNotification.action === 'accept_decline' && (
                                <>
                                    <Button
                                        variant="contained"
                                        startIcon={<CheckCircleIcon />}
                                        onClick={() => handleAction(selectedNotification, 'accept')}
                                        disabled={processing}
                                        sx={{
                                            borderRadius: '12px',
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            background: `linear-gradient(135deg, ${palette.pine}, ${palette.pineSoft})`,
                                            boxShadow: '0 4px 16px rgba(47,89,66,0.3)',
                                            '&:hover': {
                                                boxShadow: '0 8px 24px rgba(47,89,66,0.4)',
                                                transform: 'translateY(-2px)',
                                            }
                                        }}
                                    >
                                        Accept
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        startIcon={<CancelIcon />}
                                        onClick={() => handleAction(selectedNotification, 'decline')}
                                        disabled={processing}
                                        sx={{
                                            borderRadius: '12px',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            borderColor: palette.clay,
                                            color: palette.clay,
                                            '&:hover': {
                                                bgcolor: `${palette.clay}08`,
                                                transform: 'translateY(-2px)',
                                            }
                                        }}
                                    >
                                        Decline
                                    </Button>
                                </>
                            )}
                            {selectedNotification.action === 'view' && (
                                <Button
                                    variant="contained"
                                    startIcon={<VisibilityIcon />}
                                    onClick={() => handleAction(selectedNotification, 'view')}
                                    sx={{
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                                        boxShadow: '0 4px 16px rgba(193,84,31,0.3)',
                                        '&:hover': {
                                            boxShadow: '0 8px 24px rgba(193,84,31,0.4)',
                                            transform: 'translateY(-2px)',
                                        }
                                    }}
                                >
                                    View Details
                                </Button>
                            )}
                            {selectedNotification.action === 'chat' && (
                                <Button
                                    variant="contained"
                                    startIcon={<ChatIcon />}
                                    onClick={() => handleAction(selectedNotification, 'chat')}
                                    sx={{
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        background: `linear-gradient(135deg, ${palette.teal}, ${palette.tealLight})`,
                                        boxShadow: '0 4px 16px rgba(31,107,115,0.3)',
                                        '&:hover': {
                                            boxShadow: '0 8px 24px rgba(31,107,115,0.4)',
                                            transform: 'translateY(-2px)',
                                        }
                                    }}
                                >
                                    Chat Now
                                </Button>
                            )}
                            {selectedNotification.action === 'view_meeting' && (
                                <Button
                                    variant="contained"
                                    startIcon={<EventIcon />}
                                    onClick={() => handleAction(selectedNotification, 'view_meeting')}
                                    sx={{
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        background: `linear-gradient(135deg, ${palette.gold}, ${palette.goldLight})`,
                                        boxShadow: '0 4px 16px rgba(184,132,42,0.3)',
                                        '&:hover': {
                                            boxShadow: '0 8px 24px rgba(184,132,42,0.4)',
                                            transform: 'translateY(-2px)',
                                        }
                                    }}
                                >
                                    View Meeting
                                </Button>
                            )}
                            {selectedNotification.action === 'view_notification' && selectedNotification.link && (
                                <Button
                                    variant="contained"
                                    startIcon={<LinkIcon />}
                                    onClick={() => openSafeExternalUrl(selectedNotification.link)}
                                    sx={{
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        background: `linear-gradient(135deg, ${palette.teal}, ${palette.tealLight})`,
                                        boxShadow: '0 4px 16px rgba(31,107,115,0.3)',
                                        '&:hover': {
                                            boxShadow: '0 8px 24px rgba(31,107,115,0.4)',
                                            transform: 'translateY(-2px)',
                                        }
                                    }}
                                >
                                    Open Link
                                </Button>
                            )}
                        </DialogActions>
                    </>
                )}
            </Dialog>

            <style>
                {`
                    @keyframes pulse {
                        0%, 100% { opacity: 1; transform: scale(1); }
                        50% { opacity: 0.5; transform: scale(0.8); }
                    }
                    @keyframes auroraDrift {
                        0% { background-position: 0% 0%; }
                        50% { background-position: 100% 60%; }
                        100% { background-position: 0% 0%; }
                    }
                    @keyframes grainDrift {
                        0% { transform: translate(0, 0); }
                        20% { transform: translate(-2%, 2%); }
                        40% { transform: translate(2%, -1%); }
                        60% { transform: translate(-1%, -2%); }
                        80% { transform: translate(1%, 1%); }
                        100% { transform: translate(0, 0); }
                    }
                `}
            </style>
        </Box>
    );
};

export default NotificationsPage;